/**
 * The verb bar — the action channel's input, sibling to the dice bar.
 *
 * The closed set comes from `GET /api/verbs` and is never duplicated here:
 * the tier and the time cost are rules, and a stale copy in the client would
 * still render as a confident number.
 *
 * Selecting a verb attaches a token to the composer rather than sending
 * anything, so free text stays *elaboration* — the server never parses it for
 * intent, and the UI should not suggest otherwise.
 */

import { useEffect, useState } from 'react';

import type {
  CombatActionDescriptor,
  CombatConditionDescriptor,
  VerbDescriptor,
} from '@portal/shared';

import { api } from '../api.js';
import { verbCostDetail, verbCostLabel, verbLabel } from './cost.js';

/**
 * Which verbs sit on the bar, and in what order.
 *
 * Presentation only — the set itself is the server's. Anything the server
 * sends that is not named here still appears, under the overflow, so a verb
 * added to the ruleset can never go missing from the UI.
 */
const PROMINENT = ['look', 'listen', 'search', 'move', 'talk', 'take'];

/**
 * Both closed sets, from one request.
 *
 * Fetched together because which one applies is the *server's* decision, made
 * from the mode it holds — and a client that loaded them separately could be
 * rendering one while the table is in the other.
 */
export function useActionCatalogue(): {
  verbs: readonly VerbDescriptor[];
  combatActions: readonly CombatActionDescriptor[];
  conditions: readonly CombatConditionDescriptor[];
  /** The party stances Table 10.7 has a column for. */
  reactionDispositions: readonly string[];
} {
  const [catalogue, setCatalogue] = useState<{
    verbs: readonly VerbDescriptor[];
    combatActions: readonly CombatActionDescriptor[];
    conditions: readonly CombatConditionDescriptor[];
    reactionDispositions: readonly string[];
  }>({ verbs: [], combatActions: [], conditions: [], reactionDispositions: [] });

  useEffect(() => {
    let live = true;
    void api
      .get<{
        verbs: VerbDescriptor[];
        combatActions?: CombatActionDescriptor[];
        conditions?: CombatConditionDescriptor[];
        reactionDispositions?: string[];
      }>('/api/verbs')
      .then((body) => {
        if (live)
          setCatalogue({
            verbs: body.verbs,
            combatActions: body.combatActions ?? [],
            conditions: body.conditions ?? [],
            reactionDispositions: body.reactionDispositions ?? [],
          });
      })
      // A catalogue that cannot load leaves chat and dice working, so this is
      // an empty bar rather than a broken page.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  return catalogue;
}

export interface VerbBarProps {
  readonly verbs: readonly VerbDescriptor[];
  readonly selected?: VerbDescriptor | undefined;
  readonly onSelect: (verb: VerbDescriptor) => void;
}

export function VerbBar({ verbs, selected, onSelect }: VerbBarProps) {
  const [showAll, setShowAll] = useState(false);

  const prominent = PROMINENT.map((name) => verbs.find((v) => v.verb === name)).filter(
    (v): v is VerbDescriptor => v !== undefined,
  );
  const rest = verbs.filter((v) => !PROMINENT.includes(v.verb));
  const shown = showAll ? [...prominent, ...rest] : prominent;

  if (verbs.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {shown.map((verb) => (
        <button
          key={verb.verb}
          type="button"
          title={verbCostDetail(verb)}
          aria-pressed={selected?.verb === verb.verb}
          onClick={() => onSelect(verb)}
          className={`rounded-md border px-3 py-1 text-sm ${
            selected?.verb === verb.verb
              ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-950'
              : 'border-neutral-300 dark:border-neutral-700'
          }`}
        >
          {verbLabel(verb.verb)}{' '}
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {verbCostLabel(verb)}
            {/* A house rule is marked wherever its number is shown. */}
            {verb.houseRule && <span title="house rule, not printed in the book">†</span>}
          </span>
        </button>
      ))}

      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((open) => !open)}
          className="rounded-md px-2 py-1 text-sm underline underline-offset-2"
        >
          {showAll ? 'Fewer' : `More (${rest.length})`}
        </button>
      )}
    </div>
  );
}
